import { useCallback, useEffect } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import ReconnectingWebSocket from "reconnecting-websocket";
import { LoginState } from "../atoms/authAtom";
import {
  ChatLogs,
  CurrentChat,
  MessageCenterState,
} from "../atoms/messageCenterAtom";
import { getRegenToken } from "../utils/jwt";
import { updateUserFromMessage } from "../utils/messageCenterState";

import { useGetConnectionList } from "../hooks/useMessageCenter";

const SOCKET_URL = process.env.REACT_APP_MESSAGE_WEBSOCKET_URL;

const Socket = () => {
  const isLoggedIn = useRecoilValue(LoginState);
  const setMessages = useSetRecoilState(ChatLogs);
  const setCurrentChat = useSetRecoilState(CurrentChat);
  const setMessageCenterStatus = useSetRecoilState(MessageCenterState);
  const { data, refetch } = useGetConnectionList();

  const urlProvider = useCallback(() => {
    const token = getRegenToken();
    return `${SOCKET_URL}?token=${token}&baseUrl=${encodeURIComponent(
      process.env.REACT_APP_API_URL
    )}`;
  }, []);

  const handleChatDataLoad = useCallback(() => {
    if (data) {
      setMessages(data);
      setCurrentChat(data?.users?.[0]);
    }
  }, [data, setMessages, setCurrentChat]);

  useEffect(() => {
    handleChatDataLoad();
  }, [handleChatDataLoad]);

  useEffect(() => {
    if (isLoggedIn || getRegenToken()) {
      const url = urlProvider();
      const socket = new ReconnectingWebSocket(url, [], {
        maxRetries: 10,
      });

      let heartbeatInterval;

      socket.onopen = () => {
        setMessageCenterStatus("Connected");

        const delay = 4 * 60 * 1000; // 4 minutes

        // wait 4 minutes, then start the interval
        setTimeout(() => {
          heartbeatInterval = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({
                action: "ping",
                baseUrl: process.env.REACT_APP_API_URL
              }));
            }
          }, delay);
        }, delay);
      };

      socket.onmessage = (event) => {
        const messageData = JSON.parse(event.data);
        const conversationId = messageData.key;
        if (conversationId) {
          data?.users?.length === 0
            ? refetch()
            : setMessages((prevState) =>
              updateUserFromMessage(prevState, conversationId, messageData)
            );
        }
      };

      socket.onclose = () => {
        setMessageCenterStatus("Disconnected");
        clearInterval(heartbeatInterval); // ✅ Stop heartbeat
      };

      socket.onerror = (error) => {
        setMessageCenterStatus("Error");
      };

      return () => {
        socket.close();
        clearInterval(heartbeatInterval); // ✅ Ensure cleanup on unmount
      };
    }
  }, [
    isLoggedIn,
    setMessageCenterStatus,
    urlProvider,
    setMessages,
    refetch,
    data,
  ]);


  return null;
};

export default Socket;
